import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Clock, Search } from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import Nr2jTaskDrawer from '../../components/nr2j/Nr2jTaskDrawer';
import Nr2jPlanFollowUpModal from '../../components/nr2j/Nr2jPlanFollowUpModal';
import Nr2jOwnerCell from '../../components/nr2j/Nr2jOwnerCell.jsx';
import Nr2jOwnerFilterSelect from '../../components/nr2j/Nr2jOwnerFilterSelect.jsx';
import { useNr2jOwnerFilter } from '../../hooks/useNr2jOwnerFilter.js';
import apiService from '../../services/api';
import {
  formatNr2jDate,
  NR2J_TASK_TYPES,
  nr2jTaskStatusLabel,
  nr2jTaskTypeLabel,
  organizationDisplayName,
} from '../../utils/nr2jConstants';

const FILTERS = [
  { id: 'today', label: 'Aujourd’hui' },
  { id: 'upcoming', label: 'À venir' },
  { id: 'overdue', label: 'En retard' },
  { id: 'done', label: 'Terminées' },
  { id: 'all', label: 'Toutes' },
];

function activeFilterForDueAt(dueAt) {
  if (dueAt && new Date(dueAt).getTime() < Date.now()) return 'overdue';
  return 'upcoming';
}

export default function Nr2jTasks() {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'upcoming';
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [followUpOrg, setFollowUpOrg] = useState(null);
  const [followUpConflict, setFollowUpConflict] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const ownerFilter = useNr2jOwnerFilter(tasks);
  const { params: ownerParams } = ownerFilter;

  const setFilter = useCallback((id) => {
    setParams(id === 'upcoming' ? {} : { filter: id });
  }, [setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jTasks({ filter, ...ownerParams });
      setTasks(res?.tasks || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les tâches');
    } finally {
      setLoading(false);
    }
  }, [filter, ownerParams]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tasks.filter((task) => {
      if (typeFilter && task.type !== typeFilter) return false;
      if (!needle) return true;
      const blob = [
        task.title,
        task.notes,
        organizationDisplayName(task.organization),
        nr2jTaskTypeLabel(task.type),
        task.ownerUser ? `${task.ownerUser.prenom || ''} ${task.ownerUser.nom || ''}` : '',
      ].join(' ').toLowerCase();
      return blob.includes(needle);
    });
  }, [tasks, q, typeFilter]);

  async function saveTask(patch) {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await apiService.updateNr2jTask(selected.id, patch);
      const updated = res?.task || res;
      setSelected(updated);
      toast.success('Tâche mise à jour');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setBusy(false);
    }
  }

  async function completeTask(taskId) {
    setBusy(true);
    try {
      const res = await apiService.completeNr2jTask(taskId);
      setSelected(res?.task || res);
      toast.success('Tâche terminée');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setBusy(false);
    }
  }

  async function reopenTask(task) {
    const taskId = task?.id || selected?.id;
    if (!taskId) return;
    setBusy(true);
    try {
      const res = await apiService.updateNr2jTask(taskId, { status: 'OPEN' });
      const updated = res?.task || res;
      toast.success('Tâche remise à faire');
      setSelected(null);
      const nextFilter = activeFilterForDueAt(updated.dueAt);
      if (nextFilter === filter) {
        await load();
      } else {
        setFilter(nextFilter);
      }
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setBusy(false);
    }
  }

  async function cancelTask(taskId) {
    if (!window.confirm('Annuler cette tâche ?')) return;
    setBusy(true);
    try {
      const res = await apiService.updateNr2jTask(taskId, { status: 'CANCELLED' });
      setSelected(res?.task || res);
      toast.success('Tâche annulée');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setBusy(false);
    }
  }

  async function submitFollowUp(payload, updateExisting = false) {
    if (!followUpOrg?.id) return;
    setFollowUpBusy(true);
    try {
      await apiService.planNr2jFollowUp(followUpOrg.id, {
        ...payload,
        updateExisting,
      });
      toast.success(updateExisting ? 'Relance modifiée' : 'Relance planifiée');
      setFollowUpOrg(null);
      setFollowUpConflict(null);
      setSelected(null);
      const nextFilter = activeFilterForDueAt(payload.dueAt);
      if (nextFilter === filter) await load();
      else setFilter(nextFilter);
    } catch (error) {
      const existingTask = error?.data?.error?.existingTask || error?.response?.data?.error?.existingTask;
      if (error?.status === 409 || error?.data?.error?.code === 'FOLLOWUP_EXISTS' || existingTask) {
        setFollowUpConflict({
          message: error?.message,
          existingTask,
        });
      } else {
        toast.error(error?.message || 'Impossible de planifier la relance');
      }
    } finally {
      setFollowUpBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="nr2j-tasks">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tâches NR2J</h1>
        <p className="text-sm text-gray-600">Relances et actions commerciales</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              filter === f.id
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une tâche ou une société…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Tous les types</option>
          {NR2J_TASK_TYPES.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        {ownerFilter.showOwner ? (
          <Nr2jOwnerFilterSelect
            value={ownerFilter.value}
            onChange={ownerFilter.setValue}
            options={ownerFilter.options}
          />
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : visible.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Aucune tâche dans ce filtre.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {visible.map((task) => {
              const overdue =
                task.status === 'OPEN'
                && task.dueAt
                && new Date(task.dueAt).getTime() < Date.now();
              const orgId = task.organizationId || task.organization?.id;
              return (
                <li key={task.id} className="flex items-stretch gap-2 px-4 py-3 hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setSelected(task)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="text-sm font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-gray-500">
                      {nr2jTaskTypeLabel(task.type)} · {nr2jTaskStatusLabel(task.status)}
                    </p>
                    <p className={`mt-1 inline-flex items-center gap-1 text-xs ${overdue ? 'text-rose-700' : 'text-gray-500'}`}>
                      <Clock className="h-3.5 w-3.5" />
                      {formatNr2jDate(task.dueAt)}
                      {overdue ? ' · en retard' : ''}
                    </p>
                  </button>
                  <div className="flex flex-col items-end justify-center gap-1">
                    {ownerFilter.showOwner ? (
                      <Nr2jOwnerCell
                        owner={task.ownerUser}
                        linkable={ownerFilter.canOpenMember}
                        viewerId={ownerFilter.viewerId}
                        compact
                      />
                    ) : null}
                    {orgId ? (
                      <Link
                        to={`/nr2j/clients/${orgId}`}
                        className="text-xs font-medium text-indigo-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {organizationDisplayName(task.organization)}
                      </Link>
                    ) : null}
                    {task.status === 'OPEN' ? (
                      <button
                        type="button"
                        onClick={() => completeTask(task.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Terminer
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">{nr2jTaskStatusLabel(task.status)}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected ? (
        <Nr2jTaskDrawer
          task={selected}
          busy={busy}
          onClose={() => setSelected(null)}
          onSave={saveTask}
          onComplete={() => completeTask(selected.id)}
          onReopen={() => reopenTask(selected)}
          onCancel={() => cancelTask(selected.id)}
          onPlanFollowUp={() => {
            const orgId = selected.organizationId || selected.organization?.id;
            if (!orgId) return;
            setFollowUpConflict(null);
            setFollowUpOrg({
              id: orgId,
              name: organizationDisplayName(selected.organization),
            });
          }}
        />
      ) : null}

      <Nr2jPlanFollowUpModal
        open={Boolean(followUpOrg)}
        busy={followUpBusy}
        organizationName={followUpOrg?.name}
        existingConflict={followUpConflict}
        onClose={() => {
          setFollowUpOrg(null);
          setFollowUpConflict(null);
        }}
        onSubmit={(payload) => submitFollowUp(payload, false)}
        onUpdateExisting={(payload) => submitFollowUp(payload, true)}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
